import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setIsLoading, setControlsPosition } from "../../Redux/tutorialSlice";
import useTutorialSteps from "./useTutorialSteps";
import tutorialSteps from "./tutorialSteps";
import confetti from "canvas-confetti";

const useCutout = () => {
  const dispatch = useDispatch();
  const currentStep = useSelector((state) => state.tutorial.currentStep);
  const isTutorialActive = useSelector(
    (state) => state.tutorial.isTutorialActive
  );
  const { handleNextStep } = useTutorialSteps();
  const latestStepId = useRef(currentStep);
  const latestCutoutRequest = useRef(0);
  const confettiRef = useRef(null);
  const hasFiredConfetti = useRef(false);
  const previousSide = useRef(null);
  const resizeTimer = useRef(null);

  useEffect(() => {
    latestStepId.current = currentStep;
  }, [currentStep]);

  useEffect(() => {
    const canvas = document.getElementById("confetti-container");
    if (canvas && !confettiRef.current) {
      confettiRef.current = confetti.create(canvas, {
        resize: true,
        useWorker: false,
      });
    }
  }, []);

  const launchConfetti = () => {
    if (confettiRef.current && !hasFiredConfetti.current) {
      hasFiredConfetti.current = true;
      setTimeout(() => {
        confettiRef.current({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
        });
      }, 250);
    }
  };

  useEffect(() => {
    const isLastStep = currentStep === tutorialSteps.length - 1;
    if (isLastStep) {
      launchConfetti();
    } else {
      hasFiredConfetti.current = false; // reset if user goes backward
    }
  }, [currentStep]);

  const waitForScrollAndLayout = (element) =>
    new Promise((resolve) => {
      const container = document.querySelector(".practice-list");
      const scrollParent =
        container || document.scrollingElement || document.documentElement;

      const rect = element.getBoundingClientRect();
      const isInsideModal = !!element.closest(".ant-modal-body");

      const HEADER_DANGER_ZONE = 180;
      const isObstructedByHeader = rect.top < HEADER_DANGER_ZONE;

      const isStepAfter18 = currentStep > 18 || currentStep !== 0;

      if (isInsideModal) {
        requestAnimationFrame(() =>
          requestAnimationFrame(() => resolve(element.getBoundingClientRect()))
        );
        return;
      }

      if (
        (!isObstructedByHeader || !isStepAfter18) &&
        rect.bottom <= window.innerHeight
      ) {
        requestAnimationFrame(() =>
          requestAnimationFrame(() => resolve(element.getBoundingClientRect()))
        );
        return;
      }

      let timeoutId;

      const finish = () => {
        scrollParent.removeEventListener("scroll", onScroll);
        clearTimeout(timeoutId);

        // Add slight delay to allow layout settle before measuring
        setTimeout(() => {
          requestAnimationFrame(() =>
            requestAnimationFrame(() =>
              resolve(element.getBoundingClientRect())
            )
          );
        }, 100); // 100–200ms buffer
      };

      const onScroll = () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(finish, 80);
      };

      scrollParent.addEventListener("scroll", onScroll);

      const elRect = element.getBoundingClientRect();
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      const viewportHeight = window.innerHeight;
      const padding = 20;

      const targetY =
        scrollY + elRect.top + elRect.height / 2 - viewportHeight / 2;

      window.scrollTo({
        top: Math.max(targetY - padding, 0),
        behavior: "smooth",
      });

      timeoutId = setTimeout(finish, 1000);
    });

  const waitForElement = (selector, callback) => {
    const existing = document.querySelector(selector);
    if (existing) {
      callback(existing);
      return;
    }

    const observer = new MutationObserver(() => {
      const found = document.querySelector(selector);
      if (found) {
        observer.disconnect();
        callback(found);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    setTimeout(() => {
      observer.disconnect();
      callback(null);
    }, 3000);
  };

  const applyCutoutStyles = (targetRect) => {
    const expandedWidth =
      targetRect.width < 62 ? targetRect.width * 0.3 : targetRect.width * 0.04;
    const expandedHeight =
      targetRect.height < 35
        ? targetRect.height * 0.3
        : targetRect.height > 500
        ? targetRect.height * 0.05
        : targetRect.height * 0.1;

    const container = document.querySelector(".ant-modal-body");
    const containerRect = container?.getBoundingClientRect() || null;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 20;

    let cutoutTop = Math.max(targetRect.top - expandedHeight / 2, padding);
    let cutoutLeft = Math.max(targetRect.left - expandedWidth / 2, padding);
    let cutoutHeight = targetRect.height + expandedHeight;
    let cutoutWidth = targetRect.width + expandedWidth;

    // Clamp height/width to stay inside viewport with padding
    const bottomEdge = cutoutTop + cutoutHeight;
    const rightEdge = cutoutLeft + cutoutWidth;

    if (bottomEdge > viewportHeight) {
      cutoutHeight = Math.max(viewportHeight - cutoutTop, 10);
    }

    if (rightEdge > viewportWidth) {
      cutoutWidth = Math.max(viewportWidth - cutoutLeft, 10);
    }

    if (containerRect) {
      const cutoutBottom = cutoutTop + cutoutHeight;

      const intersectsModal =
        cutoutBottom > containerRect.top && cutoutTop < containerRect.bottom;

      if (intersectsModal) {
        const clampedTop = Math.max(cutoutTop, containerRect.top);
        const clampedBottom = Math.min(cutoutBottom, containerRect.bottom);

        const clampedHeight = clampedBottom - clampedTop;
        if (clampedHeight > 10) {
          cutoutTop = clampedTop;
          cutoutHeight = clampedHeight;
        }
      }
    }

    // Set cutout properties
    document.documentElement.style.setProperty(
      "--cutout-top",
      `${Math.round(cutoutTop)}px`
    );
    document.documentElement.style.setProperty(
      "--cutout-left",
      `${Math.round(cutoutLeft)}px`
    );
    document.documentElement.style.setProperty(
      "--cutout-width",
      `${Math.round(cutoutWidth)}px`
    );
    document.documentElement.style.setProperty(
      "--cutout-height",
      `${Math.round(cutoutHeight)}px`
    );

    document.documentElement.style.setProperty(
      "--tutorial-left",
      `${Math.max(targetRect.left + targetRect.width / 2 - 200, 20)}px`
    );

    const outlineEl = document.querySelector(".tutorial-outline");
    if (outlineEl) {
      const isZero = cutoutWidth < 1 || cutoutHeight < 1;
      const MIN_DIMENSION = 10;
      const safeWidth = Math.max(cutoutWidth, MIN_DIMENSION);
      const safeHeight = Math.max(cutoutHeight, MIN_DIMENSION);
      const isEdgeAligned =
        Math.abs(cutoutTop) < 2 ||
        Math.abs(cutoutLeft) < 2 ||
        Math.abs(window.innerHeight - (cutoutTop + safeHeight)) < 2;

      if (safeHeight < 8 || safeWidth < 8 || isEdgeAligned) {
        // hide cutout
        outlineEl.style.opacity = "0";
        outlineEl.style.boxShadow = "none";
        outlineEl.style.width = "0px";
        outlineEl.style.height = "0px";
        outlineEl.style.top = "0px";
        outlineEl.style.left = "0px";
      } else {
        outlineEl.style.top = `${Math.round(cutoutTop)}px`;
        outlineEl.style.left = `${Math.round(cutoutLeft)}px`;
        outlineEl.style.width = `${Math.round(safeWidth)}px`;
        outlineEl.style.height = `${Math.round(safeHeight)}px`;
        outlineEl.style.opacity = "1";
        outlineEl.style.boxShadow =
          "0 0 0 3.5px maroon, 0 0 10px 4px rgba(255, 255, 255, 0.8)";
      }

      if (isZero) {
        outlineEl.style.opacity = "0";
        outlineEl.style.boxShadow = "none";
      } else {
        outlineEl.style.opacity = "1";
        outlineEl.style.boxShadow =
          "0 0 0 3.5px maroon, 0 0 10px 4px rgba(255, 255, 255, 0.8)";
      }
    }

    // Wait for layout to settle before measuring tutorial
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const tutorialEl = document.querySelector(".tutorial-content");
        if (!tutorialEl) return;

        const _ = tutorialEl.offsetHeight; // force reflow

        const tutorialWidth = tutorialEl.offsetWidth || 400;
        const tutorialHeight = Math.max(
          tutorialEl.getBoundingClientRect().height,
          100
        ); // fallback

        const padding = 40;

        const cutoutRight = cutoutLeft + cutoutWidth;
        const cutoutBottom = cutoutTop + cutoutHeight;

        const hasSpaceBelow =
          cutoutBottom + tutorialHeight + padding <= window.innerHeight;
        const hasSpaceAbove = cutoutTop - tutorialHeight - padding >= 0;
        const hasSpaceRight =
          cutoutRight + tutorialWidth + padding <= window.innerWidth;
        const hasSpaceLeft = cutoutLeft - tutorialWidth - padding >= 0;

        let top, left;
        let chosenSide = previousSide.current;

        if (chosenSide === "below" && hasSpaceBelow) {
          top = cutoutBottom + padding;
        } else if (chosenSide === "above" && hasSpaceAbove) {
          top = cutoutTop - tutorialHeight - padding;
        } else if (hasSpaceBelow) {
          top = cutoutBottom + padding;
          chosenSide = "below";
        } else if (hasSpaceAbove) {
          top = cutoutTop - tutorialHeight - padding;
          chosenSide = "above";
        } else if (hasSpaceRight) {
          top = cutoutTop;
          left = cutoutRight + padding;
          chosenSide = "right";
        } else if (hasSpaceLeft) {
          top = cutoutTop;
          left = cutoutLeft - tutorialWidth - padding;
          chosenSide = "left";
        } else {
          top = 30;
          left = window.innerWidth - tutorialWidth - 30;
          chosenSide = "floating";
        }

        if (chosenSide === "above" || chosenSide === "below") {
          left = Math.max(
            padding,
            Math.min(
              cutoutLeft + cutoutWidth / 2 - tutorialWidth / 2,
              window.innerWidth - tutorialWidth - padding
            )
          );
        }

        if (chosenSide === "above" || chosenSide === "below") {
          left = Math.max(
            padding,
            Math.min(
              cutoutLeft + cutoutWidth / 2 - tutorialWidth / 2,
              window.innerWidth - tutorialWidth - padding
            )
          );
        }

        const viewportTooShort =
          tutorialHeight + 2 * padding > window.innerHeight;
        const viewportTooNarrow =
          tutorialWidth + 2 * padding > window.innerWidth;

        if (
          (!hasSpaceBelow &&
            !hasSpaceAbove &&
            !hasSpaceLeft &&
            !hasSpaceRight) ||
          viewportTooShort ||
          viewportTooNarrow
        ) {
          top = 30;
          left = window.innerWidth - tutorialWidth - 30;
          chosenSide = "floating";
        }

        previousSide.current = chosenSide;
        const overflowAmount =
          top + tutorialHeight + padding - window.innerHeight;
        if (overflowAmount > 0) {
          const adjustedTop = Math.max(padding, top - overflowAmount);
          console.warn(
            `Tutorial would overflow by ${overflowAmount}px — adjusting top from ${top} to ${adjustedTop}`
          );
          top = adjustedTop;
        }
        tutorialEl.style.top = `${top}px`;
        tutorialEl.style.left = `${left}px`;
        tutorialEl.setAttribute("data-position", chosenSide);

        // // 🧪 Debug
        // console.log("📍 Tutorial top position:", top);
        // console.log("📐 Tutorial height:", tutorialHeight);
        // console.log("📏 Bottom edge:", top + tutorialHeight);
        // console.log("📺 Viewport height:", window.innerHeight);
        // console.log(
        //   "❗ Overflows viewport?",
        //   top + tutorialHeight > window.innerHeight
        // );

        // console.log("📦 Tutorial layout:", {
        //   "Has Space Below": hasSpaceBelow,
        //   "Cutout Bottom": cutoutBottom,
        //   "Tutorial Height": tutorialHeight,
        //   Total: cutoutBottom + tutorialHeight + padding,
        //   "Window Height": window.innerHeight,
        //   FinalTop: top,
        //   FinalLeft: left,
        // });
      });
    });

    document.body.classList.add("tutorial-cutout");

    dispatch(
      setControlsPosition(
        cutoutTop + cutoutHeight + 300 > window.innerHeight ? "above" : "below"
      )
    );

    console.log("📏 Final Cutout:", {
      top: cutoutTop,
      height: cutoutHeight,
      left: cutoutLeft,
      width: cutoutWidth,
    });
  };

  const updateCutout = useCallback(
    (selector, advanceType, { isOpen = false, manageLoading = true } = {}) => {
      return new Promise((resolveOuter) => {
        if (!selector) {
          if (manageLoading) dispatch(setIsLoading(false));
          resolveOuter();
          return;
        }

        if (selector === "center") {
          const tutorialEl = document.querySelector(".tutorial-content");

          // Get tutorial box dimensions so to place cutout behind it
          const tutorialWidth = tutorialEl?.offsetWidth || 400;
          const tutorialHeight = tutorialEl?.offsetHeight || 200;

          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;

          const cutoutLeft = viewportWidth / 2 - tutorialWidth / 2;
          const cutoutTop = viewportHeight / 2 - tutorialHeight / 2;

          // Position the cutout directly behind the tutorial box with 0 size
          document.documentElement.style.setProperty(
            "--cutout-top",
            `${cutoutTop}px`
          );
          document.documentElement.style.setProperty(
            "--cutout-left",
            `${cutoutLeft}px`
          );
          document.documentElement.style.setProperty("--cutout-width", `0px`);
          document.documentElement.style.setProperty("--cutout-height", `0px`);

          // Still show tutorial box centered
          if (tutorialEl) {
            tutorialEl.style.transform = ""; // clear if any
            tutorialEl.style.top = `${
              viewportHeight / 2 - tutorialHeight / 2
            }px`;
            tutorialEl.style.left = `${
              viewportWidth / 2 - tutorialWidth / 2
            }px`;
          }

          const outlineEl = document.querySelector(".tutorial-outline");
          if (outlineEl) {
            // Position it behind the tutorial window, but invisible
            outlineEl.style.top = `${cutoutTop}px`;
            outlineEl.style.left = `${cutoutLeft}px`;
            outlineEl.style.width = `0px`;
            outlineEl.style.height = `0px`;
            outlineEl.style.opacity = `0`;
            outlineEl.style.boxShadow = `none`;
          }

          document.body.classList.add("tutorial-cutout");
          document.body.classList.remove("disable-scroll");

          if (manageLoading) dispatch(setIsLoading(false));

          return Promise.resolve();
        }

        const thisStepId = currentStep;
        const thisRequestId = ++latestCutoutRequest.current;

        if (manageLoading) dispatch(setIsLoading(true));

        waitForElement(selector, async (element) => {
          if (!element || latestStepId.current !== thisStepId) {
            if (manageLoading) dispatch(setIsLoading(false));
            resolveOuter();
            return;
          }

          let elementToUse = element;

          const isGoingBackToStep11 =
            currentStep === 11 &&
            tutorialSteps[12]?.target?.includes("practice-card") &&
            selector === ".confirm-tutorial";

          if (isGoingBackToStep11) {
            const waitForModal = () =>
              new Promise((resolve) => {
                const checkVisibleModal = () => {
                  const modal = document.querySelector(".ant-modal");
                  const style = modal && window.getComputedStyle(modal);
                  const isVisible =
                    style?.display !== "none" && style?.visibility !== "hidden";

                  if (modal && isVisible) {
                    resolve();
                  } else {
                    requestAnimationFrame(checkVisibleModal);
                  }
                };

                const observer = new MutationObserver(() => {
                  const modal = document.querySelector(".ant-modal");
                  if (modal) {
                    observer.disconnect();
                    checkVisibleModal();
                  }
                });

                observer.observe(document.body, {
                  childList: true,
                  subtree: true,
                });

                checkVisibleModal(); // fallback if already there
              });

            await waitForModal();
          }

          const baseRect = await waitForScrollAndLayout(element);

          if (thisRequestId !== latestCutoutRequest.current) {
            resolveOuter();
            return;
          }

          if (advanceType === "click" && selector !== ".location-sel-next") {
            element.addEventListener("click", (e) => {
              if (e.detail > 1) return;
              e.preventDefault();
              e.stopPropagation();
              setTimeout(() => handleNextStep(e), 0);
            });
          }

          const maybeDropdown = document.querySelector(
            ".ant-select-dropdown:not(.ant-select-dropdown-hidden), .ant-dropdown:not(.ant-dropdown-hidden)"
          );

          if (maybeDropdown && isOpen) {
            const dropdownRect = maybeDropdown.getBoundingClientRect();
            const mergedRect = {
              top: Math.min(baseRect.top, dropdownRect.top),
              left: Math.min(baseRect.left, dropdownRect.left),
              width:
                Math.max(baseRect.right, dropdownRect.right) -
                Math.min(baseRect.left, dropdownRect.left),
              height:
                Math.max(baseRect.bottom, dropdownRect.bottom) -
                Math.min(baseRect.top, dropdownRect.top),
            };
            applyCutoutStyles(mergedRect);
          } else {
            applyCutoutStyles(baseRect);
          }

          if (manageLoading) {
            const needsDelay = currentStep === 5;
            if (needsDelay) {
              setTimeout(() => {
                dispatch(setIsLoading(false));
                resolveOuter();
              }, 1500);
            } else {
              dispatch(setIsLoading(false));
              resolveOuter();
            }
          } else {
            resolveOuter();
          }
        });
      });
    },
    [dispatch, currentStep]
  );

  useEffect(() => {
    if (!isTutorialActive) return;

    const handleResize = () => {
      if (resizeTimer.current) {
        clearTimeout(resizeTimer.current);
      }
      // Delay to avoid jumpiness during continuous resize
      resizeTimer.current = setTimeout(() => {
        const step = tutorialSteps[currentStep];
        if (step?.target) {
          requestAnimationFrame(() =>
            updateCutout(step.target, step.advanceType || null, {
              manageLoading: true,
            })
          );
        }
      }, 80);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      if (resizeTimer.current) clearTimeout(resizeTimer.current);
    };
  }, [isTutorialActive, currentStep, updateCutout]);

  // Force re-run cutout for step 28 after 1s
  useEffect(() => {
    if (!isTutorialActive) return;
    if (currentStep === 28) {
      const timer = setTimeout(() => {
        const step = tutorialSteps[28];
        if (step?.target) {
          updateCutout(step.target, step.advanceType || null, {
            manageLoading: true,
          });
        }
      }, 1000); // 1 second delay

      return () => clearTimeout(timer);
    }
  }, [currentStep, isTutorialActive, updateCutout]);

  return { updateCutout };
};

export default useCutout;
